import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { useIsMounted } from 'custom_hooks/useIsMounted';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import { tokens } from 'styles/tokens';
import { notificationToastZIndex } from 'styles/variables';
import { v4 as uuid } from 'uuid';

export const defaultTimeoutMs = 5000;

export type ToastType = 'success' | 'error';

const getBackgroundColor = (type: ToastType) => {
    switch (type) {
        case 'success':
            return tokens.alert.successBackground;
        case 'error':
            return tokens.alert.errorBackground;
        default:
            throw new Error('Toast type not supported');
    }
};

const getColor = (type: ToastType) => {
    switch (type) {
        case 'success':
            return tokens.alert.successColor;
        case 'error':
            return tokens.alert.errorColor;
        default:
            throw new Error('Toast type not supported');
    }
};

export type ToastOptions = Readonly<{
    /**
     * The type of toast.
     * TODO when more toast types are added, they may be differentiated by using this.
     */
    type: ToastType;
    content: ReactNode;
    /**
     * The number of milliseconds to show the toast. Defaults to `5000`.
     * If `infinity`, the toast requires being closed manually.
     */
    timeoutMs?: number | 'infinity';
}>;

export type ToastConfig = Exclude<ToastOptions, 'timeoutMs'> &
    Readonly<{
        id: string;
        timeoutHandle?: number;
    }>;

export type NotificationContext = Readonly<{
    showToast: (config: ToastOptions) => void;
}>;

const Context = createContext<NotificationContext | undefined>(undefined);

export type NotificationProviderProps = Readonly<{
    children?: ReactNode;
}>;

export function NotificationProvider({ children }: NotificationProviderProps) {
    const [toasts, setToasts] = useState<ToastConfig[]>([]);
    const isMounted = useIsMounted();

    const removeToast = useCallback(
        (id: string, timeoutHandle?: number) => {
            if (isMounted.current) {
                window.clearTimeout(timeoutHandle);
                setToasts((current) => current.filter((cfg) => cfg.id !== id));
            }
        },
        [isMounted],
    );

    const ctx = useMemo<NotificationContext>(
        () => ({
            showToast: ({
                type,
                content,
                timeoutMs = defaultTimeoutMs,
            }: ToastOptions) => {
                const id = uuid();

                const config: ToastConfig = {
                    id,
                    type,
                    content,
                    timeoutHandle:
                        timeoutMs !== 'infinity'
                            ? window.setTimeout(() => removeToast(config.id), timeoutMs)
                            : undefined,
                };

                setToasts((current) => [...current, config]);
            },
        }),
        [removeToast],
    );

    return (
        <Context.Provider value={ctx}>
            {children}
            <NotificationGroup
                style={{
                    bottom: 0,
                    left: 10,
                    alignItems: 'flex-start',
                    zIndex: notificationToastZIndex,
                }}
            >
                {toasts.map(({ id, type, content, timeoutHandle }) => (
                    <Notification
                        key={id}
                        type={{ style: type, icon: true }}
                        style={{
                            fontWeight: 400,
                            backgroundColor: getBackgroundColor(type),
                            color: getColor(type),
                            display: 'flex',
                            gap: '0.75rem',
                            alignItems: 'center',
                        }}
                        closable
                        onClose={() => removeToast(id, timeoutHandle)}
                    >
                        {content}
                    </Notification>
                ))}
            </NotificationGroup>
        </Context.Provider>
    );
}

export function useNotificationContext(): NotificationContext {
    const ctx = useContext(Context);
    if (!ctx) {
        throw new Error('Missing <NotificationProvider>');
    }
    return ctx;
}
